/**
 * Schema Inheritance Resolver
 * Resolves control inheritance by merging parent control properties into child controls
 */

import type { FormSchema, FormControl } from '@atlas-forms/types-js';

export interface InheritanceResolverConfig {
  maxDepth?: number;
  onLoadParentForm?: (formID: number) => Promise<FormSchema>;
}

/**
 * Resolves control inheritance in a form schema
 * Merges parent control properties into child controls that have referredFormID set
 */
export class SchemaInheritanceResolver {
  private maxDepth: number;
  private onLoadParentForm: (formID: number) => Promise<FormSchema>;
  private loadedSchemas = new Map<number, FormSchema>();
  private resolvedControls = new Set<string>();

  constructor(config: InheritanceResolverConfig = {}) {
    this.maxDepth = config.maxDepth || 5;
    this.onLoadParentForm = config.onLoadParentForm || (async () => ({ version: '1.0', metadata: {}, controls: [] } as unknown as FormSchema));
  }

  /**
   * Main entry point: resolve inheritance in a form schema
   */
  async resolveSchema(schema: FormSchema, currentFormID?: number): Promise<FormSchema> {
    if (!schema.controls || schema.controls.length === 0) {
      return schema;
    }

    // Process each control and resolve inheritance
    const resolvedControls = await Promise.all(
      schema.controls.map(control =>
        this.resolveControl(control, 0, currentFormID)
      )
    );

    return {
      ...schema,
      controls: resolvedControls,
    };
  }

  /**
   * Resolve a single control, merging properties from parent if needed
   */
  private async resolveControl(
    control: FormControl,
    depth: number,
    currentFormID?: number
  ): Promise<FormControl> {
    // Prevent infinite recursion
    if (depth > this.maxDepth) {
      console.warn(`[SchemaInheritanceResolver] Max depth (${this.maxDepth}) exceeded for control ${control.id}`);
      return control;
    }

    // Check if this control has inheritance
    const referredFormID = (control as any).referredFormID;
    const referredControlID = (control as any).referredControlID;
    const inheritType = (control as any).controlInheritType;

    // If no inheritance, return as-is
    if (!referredFormID || !referredControlID) {
      return control;
    }

    // Prevent circular inheritance (same form)
    if (referredFormID === currentFormID && inheritType === 'import') {
      console.warn(`[SchemaInheritanceResolver] Circular inheritance detected for control ${control.id}`);
      return control;
    }

    try {
      // Load parent form schema
      const parentSchema = await this.loadParentSchema(referredFormID);
      if (!parentSchema || !parentSchema.controls) {
        return control;
      }

      // Find the parent control(s) to inherit from
      const parentControlIDs = Array.isArray(referredControlID) ? referredControlID : [referredControlID];
      const parentControls = parentControlIDs
        .map(id => parentSchema.controls?.find(c => c.id === id))
        .filter(Boolean) as FormControl[];

      if (parentControls.length === 0) {
        console.warn(`[SchemaInheritanceResolver] Parent control ${referredControlID} not found in form ${referredFormID}`);
        return control;
      }

      // Merge parent properties into child based on inheritance type
      return this.mergeControl(control, parentControls[0], inheritType);
    } catch (error) {
      console.error(`[SchemaInheritanceResolver] Error resolving control ${control.id}:`, error);
      return control;
    }
  }

  /**
   * Load and cache parent form schema
   */
  private async loadParentSchema(formID: number): Promise<FormSchema | null> {
    // Return cached version if available
    if (this.loadedSchemas.has(formID)) {
      return this.loadedSchemas.get(formID) || null;
    }

    try {
      const schema = await this.onLoadParentForm(formID);
      this.loadedSchemas.set(formID, schema);
      return schema;
    } catch (error) {
      console.error(`[SchemaInheritanceResolver] Failed to load parent form ${formID}:`, error);
      return null;
    }
  }

  /**
   * Merge parent control properties into child control
   */
  private mergeControl(
    childControl: FormControl,
    parentControl: FormControl,
    inheritType?: string
  ): FormControl {
    if (!inheritType || inheritType === 'full' || inheritType === 'import') {
      // Full inheritance: merge all parent properties, but keep child's id
      return {
        ...parentControl,
        id: childControl.id, // Always keep child's ID
        referredFormID: (childControl as any).referredFormID,
        referredControlID: (childControl as any).referredControlID,
        controlInheritType: inheritType as any,
        // Preserve any child-specific overrides if they exist
        label: (childControl as any).label || parentControl.label,
      };
    } else if (inheritType === 'partial') {
      // Partial inheritance: selectively merge from parent
      return {
        ...childControl,
        // Copy config and options from parent if child doesn't have them
        config: {
          ...parentControl.config,
          ...((childControl as any).config || {}),
        },
        // Preserve inheritance metadata
        referredFormID: (childControl as any).referredFormID,
        referredControlID: (childControl as any).referredControlID,
        controlInheritType: inheritType as any,
      };
    } else if (inheritType === 'override') {
      // Override mode: child is independent, only use parent as template
      return childControl;
    }

    // Default: merge all properties
    return {
      ...parentControl,
      ...childControl,
      id: childControl.id,
    };
  }

  /**
   * Clear cached schemas
   */
  clearCache(): void {
    this.loadedSchemas.clear();
    this.resolvedControls.clear();
  }
}
